import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Upload, X, ImagePlus, Loader2, Save, ExternalLink } from "lucide-react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import MyPageShell, { EmptyState } from "../components/MyPageShell";

const SERVICE_ROLES = ["loan_agent", "advocate", "architect", "interior_designer", "civil_engineer", "civil_contractor", "property_valuer", "renovation"];

export default function MyPortfolio() {
    const { user } = useAuth();
    const [items, setItems] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [pendingImage, setPendingImage] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [busy, setBusy] = useState(null);
    const fileRef = useRef(null);

    const isServiceRole = user && SERVICE_ROLES.includes(user.role);

    const load = async () => {
        try {
            const r = await api.get("/me/portfolio");
            setItems(r.data || []);
        } catch { setItems([]); }
    };
    useEffect(() => { if (isServiceRole) load(); }, [isServiceRole]);

    if (!user) return null;

    if (!isServiceRole) {
        return (
            <MyPageShell title="Service Portfolio" subtitle="Available for service professionals only" testid="my-portfolio-page">
                <EmptyState
                    icon={ImagePlus}
                    title="Portfolio not available for your account"
                    message="Portfolios are only for service professionals — Loan Agent, Advocate, Architect, Interior Designer, Civil Engineer, Civil Contractor, Property Valuer or Renovation. Update your account role from Settings if needed."
                    cta="Go to Settings"
                    to="/account-settings"
                    testid="portfolio-not-service"
                />
            </MyPageShell>
        );
    }

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/properties/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPendingImage(r.data.url);
            toast.success("Image uploaded — add a title and save");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const save = async () => {
        if (!pendingImage) { toast.error("Please upload an image first"); return; }
        setSaving(true);
        try {
            await api.post("/me/portfolio", { image_url: pendingImage, title: title || null, description: description || null });
            toast.success("Added to portfolio");
            setPendingImage(null); setTitle(""); setDescription("");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this portfolio photo?")) return;
        setBusy(id);
        try {
            await api.delete(`/me/portfolio/${id}`);
            toast.success("Deleted");
            setItems((prev) => prev.filter((x) => x.id !== id));
        } catch { toast.error("Delete failed"); }
        finally { setBusy(null); }
    };

    const startEdit = (it) => {
        setEditingId(it.id); setTitle(it.title || ""); setDescription(it.description || "");
    };
    const saveEdit = async () => {
        setBusy(editingId);
        try {
            await api.patch(`/me/portfolio/${editingId}`, { title: title || null, description: description || null });
            toast.success("Updated");
            setEditingId(null); setTitle(""); setDescription("");
            load();
        } catch { toast.error("Update failed"); }
        finally { setBusy(null); }
    };

    return (
        <MyPageShell title="Service Portfolio" subtitle="Showcase your best projects — buyers see this on Explore Property Services." testid="my-portfolio-page">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-navy-500/60">
                    {items === null ? "Loading…" : `${items.length} photo${items.length !== 1 ? "s" : ""}`}
                </div>
                <Link
                    to={`/services/professional/${user.id}`}
                    target="_blank"
                    data-testid="my-portfolio-public-link"
                    className="inline-flex items-center gap-1 text-[11px] text-gold-700 font-semibold hover:underline"
                >
                    <ExternalLink className="w-3.5 h-3.5" /> Preview my public page
                </Link>
            </div>

            {/* Upload zone */}
            <div className="rounded-xl border-2 border-dashed border-gold-500/40 bg-gold-500/5 p-4 mb-4" data-testid="portfolio-upload-zone">
                {pendingImage ? (
                    <div className="space-y-3">
                        <div className="relative rounded-lg overflow-hidden bg-navy-500/5">
                            <img src={fileUrl(pendingImage)} alt="Preview" className="w-full max-h-64 object-cover" />
                            <button onClick={() => setPendingImage(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center hover:bg-white shadow" aria-label="Remove">
                                <X className="w-4 h-4 text-navy-500" />
                            </button>
                        </div>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Photo title (e.g. 3BHK Modular Kitchen — Wardha Road)" data-testid="portfolio-title-input" className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60" />
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Short description — materials used, timeline, client scope…" data-testid="portfolio-desc-input" className="w-full rounded-lg border border-navy-500/15 bg-white px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 resize-none" />
                        <button onClick={save} disabled={saving} data-testid="portfolio-save-btn" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gold-500 text-navy-500 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-60">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {saving ? "Saving…" : "Add to portfolio"}
                        </button>
                    </div>
                ) : (
                    <label className="flex flex-col items-center justify-center gap-2 py-6 cursor-pointer" data-testid="portfolio-upload-label">
                        <ImagePlus className="w-10 h-10 text-gold-600" />
                        <div className="text-sm font-bold text-navy-500">
                            {uploading ? "Uploading…" : "Tap to upload a portfolio photo"}
                        </div>
                        <div className="text-[11px] text-navy-500/55">JPG / PNG · Max 8MB</div>
                        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} disabled={uploading} className="hidden" data-testid="portfolio-file-input" />
                        <span className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-gold-500 text-navy-500 text-xs font-extrabold">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Choose photo
                        </span>
                    </label>
                )}
            </div>

            {/* Existing portfolio grid */}
            {items === null && <div className="py-8 text-center text-sm text-navy-500/50">Loading portfolio…</div>}
            {items && items.length === 0 && (
                <div className="py-6 text-center text-sm text-navy-500/50">No photos yet — upload your first project above.</div>
            )}
            {items && items.length > 0 && (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map((it) => (
                        <li key={it.id} className="rounded-xl border border-navy-500/10 bg-white overflow-hidden" data-testid={`portfolio-item-${it.id}`}>
                            <div className="aspect-[16/10] bg-navy-500/5">
                                <img src={fileUrl(it.image_url)} alt={it.title || "Portfolio"} className="w-full h-full object-cover" />
                            </div>
                            <div className="p-3">
                                {editingId === it.id ? (
                                    <div className="space-y-2">
                                        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded border border-navy-500/15 bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-gold-500/60" />
                                        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded border border-navy-500/15 bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-gold-500/60 resize-none" />
                                        <div className="flex gap-2">
                                            <button onClick={saveEdit} disabled={busy === it.id} className="flex-1 px-3 py-1.5 rounded bg-gold-500 text-navy-500 text-xs font-extrabold hover:bg-gold-400 disabled:opacity-60">
                                                Save
                                            </button>
                                            <button onClick={() => { setEditingId(null); setTitle(""); setDescription(""); }} className="px-3 py-1.5 rounded border border-navy-500/20 text-navy-500 text-xs font-semibold">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="font-heading font-bold text-navy-500 text-sm truncate">{it.title || "Untitled"}</div>
                                        {it.description && <div className="text-[11px] text-navy-500/60 line-clamp-2 mt-0.5">{it.description}</div>}
                                        <div className="mt-2 flex items-center gap-1.5">
                                            <button onClick={() => startEdit(it)} data-testid={`portfolio-edit-${it.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy-500/70 hover:text-navy-500 px-2 py-1 rounded border border-navy-500/15">
                                                <Pencil className="w-3 h-3" /> Edit
                                            </button>
                                            <button onClick={() => del(it.id)} disabled={busy === it.id} data-testid={`portfolio-delete-${it.id}`} className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 px-2 py-1 rounded border border-red-200 disabled:opacity-50">
                                                <Trash2 className="w-3 h-3" /> Delete
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </MyPageShell>
    );
}
